import { FeedbackFormSchema } from '@company/schemas/FeedbackFormSchema';
import { CloseButton } from '@components/utility/buttons';
import { useSendFeedbackMutation } from '@extendedWarranty/services/extendedWarranty';
import { FeedbackData, FeedbackDataKeys, FeedbackResponse } from '@extendedWarranty/types/extendedWarranty';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormGroup,
    TextField,
    Typography,
} from '@mui/material';
import { getValidObjectKeys, handleError } from '@utils/common';
import { useFormik } from 'formik';
import { useCallback, useMemo } from 'react';
import { FeedbackDialogActions } from './FeedbackDialogActions';
import { ExtendedWarrantyPageHeader } from './PageHeader';

type FeedbackDialogProps = {
    open: boolean;
    setOpen: (value: boolean) => void;
};

const FeedbackDialog = ({ open, setOpen }: FeedbackDialogProps) => {
    const [
        sendFeedback,
        { isLoading: isSendFeedbackLoading, isSuccess: isSendFeedbackSuccess, reset: resetSendFeedbackState },
    ] = useSendFeedbackMutation();
    const { isValid, values, resetForm, touched, handleBlur, handleChange, handleSubmit, errors, submitForm } =
        useFormik<FeedbackData>({
            initialValues: {
                first_name: '',
                last_name: '',
                email: '',
                phone_number: '',
                feedback_text: '',
            },
            validateOnMount: true,
            validationSchema: FeedbackFormSchema,
            onSubmit: async (values, formik) => {
                try {
                    await sendFeedback(values).unwrap();
                } catch (err) {
                    const error = (err as unknown as FeedbackResponse).data;
                    if (error) {
                        const errorKeys = Object.keys(error);

                        const validErrorKeys = getValidObjectKeys<FeedbackDataKeys>(errorKeys, error);

                        if (validErrorKeys && validErrorKeys.length > 0) {
                            const errors: Record<string, string> = {};
                            for (const key of validErrorKeys) {
                                errors[key] = (error[key] && error[key][0]) ?? '';
                            }
                            formik.setErrors(errors);
                            return;
                        }
                    }
                    handleError('Could not send feedback', err);
                }
            },
        });

    const disableForm = useMemo(
        () => isSendFeedbackSuccess || isSendFeedbackLoading,
        [isSendFeedbackSuccess, isSendFeedbackLoading]
    );

    const handleClose = useCallback(() => {
        setOpen(false);

        // Delay form reset to avoid floating label and disabled fields artifacts.
        setTimeout(() => {
            resetForm();
            resetSendFeedbackState();
        }, 100);
    }, [setOpen, resetForm, resetSendFeedbackState]);

    return (
        <Dialog
            className="absolute z-0"
            open={open}
            onClose={handleClose}
            disablePortal
            fullWidth
            slotProps={{
                backdrop: {
                    className: 'mt-[50px] md:mt-0 md:-z-1',
                },

                paper: {
                    className:
                        'absolute flex flex-col m-0 max-w-[90%] md:max-h-full top-[47vh] left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-sm md:p-10 md:pb-7 md:top-1/2',
                    sx: {
                        color: (theme) => theme.palette['neutral-900'].main,
                        boxShadow: 3,
                    },
                },
            }}
        >
            <CloseButton className="absolute size-10 flex" color="black" onClick={() => handleClose()} />
            <DialogTitle>
                <ExtendedWarrantyPageHeader
                    className="w-3/4 md:w-full"
                    title="Customer Feedback"
                    subtitle="Please help us improve this process by highlighting any inconsistencies or errors that you may encounter."
                />
            </DialogTitle>
            <DialogContent dividers className="md:border-0 ">
                <div className="flex flex-col gap-4 md:gap-9">
                    <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
                        <div className="flex flex-col gap-3">
                            <Typography fontSize={16} fontWeight={700}>
                                Name
                            </Typography>
                            <FormGroup className="flex md:flex-row gap-4 !items-start md:items-center">
                                <FormControl className="flex-1 w-full">
                                    <TextField
                                        disabled={disableForm}
                                        id="first_name"
                                        name="first_name"
                                        placeholder="First name"
                                        label="First Name"
                                        value={values.first_name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.first_name && Boolean(errors.first_name)}
                                        helperText={touched.first_name && errors.first_name}
                                        slotProps={{
                                            inputLabel: {
                                                required: true,
                                            },
                                        }}
                                    />
                                </FormControl>
                                <FormControl className="flex-1 w-full">
                                    <TextField
                                        disabled={disableForm}
                                        id="last_name"
                                        name="last_name"
                                        placeholder="Last name"
                                        label="Last Name"
                                        value={values.last_name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.last_name && Boolean(errors.last_name)}
                                        helperText={touched.last_name && errors.last_name}
                                        slotProps={{
                                            inputLabel: {
                                                required: true,
                                            },
                                        }}
                                    />
                                </FormControl>
                            </FormGroup>
                        </div>
                        <div className="flex flex-col gap-2 p">
                            <Typography fontSize={16} fontWeight={700}>
                                Contact
                            </Typography>
                            <FormGroup className="flex md:flex-row gap-4 items-start md:items-center">
                                <FormControl className="flex-1 w-full">
                                    <TextField
                                        disabled={disableForm}
                                        type="email"
                                        id="email"
                                        name="email"
                                        placeholder="Email"
                                        label="Email"
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.email && Boolean(errors.email)}
                                        helperText={touched.email && errors.email}
                                        slotProps={{
                                            inputLabel: {
                                                required: true,
                                            },
                                        }}
                                    />
                                </FormControl>
                                <FormControl className="flex-1 w-full">
                                    <TextField
                                        disabled={disableForm}
                                        id="phone_number"
                                        name="phone_number"
                                        placeholder="Phone number"
                                        label="Phone number"
                                        value={values.phone_number}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.phone_number && Boolean(errors.phone_number)}
                                        helperText={touched.phone_number && errors.phone_number}
                                    />
                                </FormControl>
                            </FormGroup>
                        </div>
                        <FormControl className="flex flex-col gap-2 w-full">
                            <div className="flex gap-1">
                                <Typography fontSize={16} fontWeight={700}>
                                    Message
                                </Typography>
                                <Typography color="error-400.main">*</Typography>
                            </div>
                            <TextField
                                disabled={disableForm}
                                multiline
                                minRows={7}
                                id="feedback_text"
                                name="feedback_text"
                                placeholder="Leave your feedback here."
                                value={values.feedback_text}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.feedback_text && Boolean(errors.feedback_text)}
                                helperText={touched.feedback_text && errors.feedback_text}
                            />
                        </FormControl>
                    </form>
                    <FeedbackDialogActions
                        className="md:hidden"
                        isValid={isValid}
                        isSendFeedbackLoading={isSendFeedbackLoading}
                        isSendFeedbackSuccess={isSendFeedbackSuccess}
                        disableForm={disableForm}
                        submitForm={submitForm}
                    />
                </div>
            </DialogContent>
            <DialogActions className="hidden md:flex px-6">
                <FeedbackDialogActions
                    isValid={isValid}
                    isSendFeedbackLoading={isSendFeedbackLoading}
                    isSendFeedbackSuccess={isSendFeedbackSuccess}
                    disableForm={disableForm}
                    submitForm={submitForm}
                />
            </DialogActions>
        </Dialog>
    );
};

export { FeedbackDialog };
